import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  spacing,
  breakpoints,
  palette,
  Typography,
  Button,
  fontStyle,
} from '@playdapp/ui';
import { useInView } from 'react-intersection-observer';
import { useInfiniteQuery } from 'react-query';
import { shallowEqual } from 'react-redux';
import { useMedia } from 'react-use';
import times from 'lodash/times';
import classNames from 'classnames';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  setIsSwitchBundle,
  setSelectedMySalesCollection,
  setSelectedMySalesOrderFilter,
} from 'store/my-account';
import useTokenCurrency from 'hooks/useTokenCurrency';
import { getUserBundleList } from 'api/item';
import getMyItemsData from 'api/graphql/getMyAssets';
import { chainIdToMainNetworkName } from 'lib/network';
import { numberToAbbreviateString } from 'lib/util';

import type { OrderStatus } from 'types/assets';

import EmptyItem from '@/components/EmptyItem';
import SortByDropDown from '@/components/SortByDropDown';
import Icon from '@/components/Icon';
import Card from '@/components/Card';
import Image from '@/components/Image';
import MetaTag from '@/components/MetaTag';
import ErrorContents from '@/components/ErrorContents';

type Props = {
  salesType: 'bundle' | 'single';
};

const SalesOptionBox = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  overflow-x: hidden;

  div {
    display: flex;
    align-items: center;
    height: 100%;
    width: 100%;
    min-width: 420px;

    ${breakpoints.down('xl')} {
      min-width: 100%;
    }

    button + button {
      margin-left: ${spacing.xs};
    }
  }

  ${breakpoints.down('md')} {
    order: 2;
  }
`;

const CollectionDrop = styled(SortByDropDown)`
  position: relative;
  align-items: flex-end;
  ${fontStyle('b4')};

  ${breakpoints.down('lg')} {
    display: none;
  }
`;

const MobileCollectionDrop = styled(SortByDropDown)`
  display: none;

  ${breakpoints.down('lg')} {
    display: block;
    align-items: flex-end;
    width: 100%;
    max-width: ${({ theme }) => theme.rem(200)};
    order: 2;
  }

  ${breakpoints.down('md')} {
    display: block;
    align-items: flex-end;
    width: 100%;
    max-width: 100%;
    margin-right: ${spacing.m};
    order: 1;

    div {
      ${fontStyle('p3')};
    }
  }
`;

const DropDownSortContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-radius: ${spacing.xs};
  height: 500px;
  padding: 0;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`;

const DropDownItem = styled.button`
  display: flex;
  align-items: center;
  padding: ${spacing.s} ${spacing.l};
  outline: 0;
  border-radius: ${spacing.xs};
  background-color: transparent;
  text-align: left;
  font-family: initial;

  &:hover {
    background-color: ${palette.gray400};
  }

  & > span {
    flex: none;
    border-radius: 50%;
  }

  & > p {
    margin-left: ${spacing.s};
  }

  ${breakpoints.down('md')} {
    padding: ${spacing.s};
  }
`;

const CustomButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;

  :hover {
    background: rgba(43, 202, 176, 0.08);
  }

  & + a,
  a + & {
    margin-left: ${spacing.xs};
  }

  &.selected {
    background: rgba(43, 202, 176, 0.2);
    :hover {
      background: rgba(43, 202, 176, 0.2);
    }
  }

  ${breakpoints.down('md')} {
    ${fontStyle('p3')};
    padding: ${spacing.xs} ${spacing.s};
  }
`;

const SelectBundleArea = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  flex: auto;
  text-align: center;

  ${breakpoints.down('md')} {
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: ${spacing.m};
    order: 1;
  }
`;

const SelectBundleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  height: ${({ theme }) => theme.rem(40)};
  padding: ${spacing.xs};
  border: 1px solid ${palette.gray700};
  border-radius: ${spacing.xl};
  margin-right: ${spacing.xs};

  ${breakpoints.down('lg')} {
    padding: ${spacing.xs};
    order: 2;
  }

  ${breakpoints.down('md')} {
    width: auto;
    height: auto;
    padding: ${spacing.xxs} ${spacing.xs};
    order: 2;
  }
`;

const SelectBundleItem = styled(Button)`
  display: flex;
  align-items: center;
  padding: ${spacing.xs} ${({ theme }) => theme.rem(10)};
  height: ${spacing.xxl};
  text-transform: capitalize;

  & > span {
    margin-right: 0 !important;
  }

  & > p {
    margin-left: ${spacing.xs};
  }

  ${breakpoints.down('md')} {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: auto;
    height: auto;

    & span {
      margin-right: 0;
    }

    & > p {
      display: none;
    }
  }
`;

const SelectOptionArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${({ theme }) => theme.rem(40)};
  margin-bottom: ${spacing.xl};

  ${breakpoints.up('md')} {
    display: flex;
    align-items: center;
    height: auto;
  }

  ${breakpoints.down('md')} {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    height: auto;
    margin-bottom: ${spacing.m};
  }
`;

const ContentBody = styled.div`
  width: 100%;
  min-height: ${({ theme }) => theme.rem(540)};
`;

const CardListBox = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: ${spacing.l};

  ${breakpoints.down('xxl')} {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: ${spacing.m};
  }

  ${breakpoints.down('xl')} {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: ${spacing.l};
  }

  ${breakpoints.down('lg')} {
    gap: ${spacing.l};
    margin-top: ${spacing.l};
  }

  ${breakpoints.down('md')} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing.m};
  }

  @media screen and (max-width: 359px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${spacing.s};
  }
`;

const EmptyItemBlock = styled.div`
  margin-top: 10rem;
`;

const MySales = ({ salesType }: Props) => {
  const isMobile = useMedia('(max-width: 479px)', true);
  const [isClose, setIsClose] = useState(false);

  const { collectionList } = useAppSelector(({ collections }) => collections);
  const { account, activeChainId } = useAppSelector(({ wallet }) => wallet);
  const {
    selectedMySalesOrderFilter,
    selectedMySalesCollection,
    isSwitchBundle,
  } = useAppSelector(({ myAccount }) => myAccount, shallowEqual);
  const [loadMoreRef, loadMoreRefInView] = useInView();

  const dispatch = useAppDispatch();

  const { convertToUsd } = useTokenCurrency();

  const fetchSingleItemList = async ({ pageParam = 1 }) => {
    if (!account || !activeChainId) {
      // TODO: 예외처리
      throw new Error('No account or chainId');
    }

    const res = await getMyItemsData({
      from: (pageParam - 1) * 30,
      count: 30,
      andOperations: [
        { key: 'owner', value: account },
        { key: 'network', value: chainIdToMainNetworkName[activeChainId] },
        { key: 'isInBundle', value: 'false' },
        { key: 'order.sale', value: '$exist' },
      ],
      orOperations: [
        Object.values(selectedMySalesOrderFilter).some((value) => value)
          ? Object.keys(selectedMySalesOrderFilter).map((orderItem) => ({
              key: `order.${orderItem}`,
              value: `$${
                !selectedMySalesOrderFilter[orderItem as OrderStatus]
                  ? 'not_'
                  : ''
              }exist`,
            }))
          : [
              { key: 'order.sale', value: '$exist' },
              { key: 'order.auction', value: '$exist' },
              { key: 'order.offer', value: '$exist' },
            ],
        selectedMySalesCollection
          ? selectedMySalesCollection.assets.map((id) => ({
              key: 'assetId',
              value: id.toString(),
            }))
          : [],
      ],
    });

    return { ...res, from: (pageParam - 1) * 30, nextPage: pageParam + 1 };
  };

  const fetchBundleItemList = async ({ pageParam = 1 }) => {
    if (!activeChainId) {
      // TODO: 예외처리
      throw new Error('No account or chainId');
    }

    const res = await getUserBundleList({
      network: chainIdToMainNetworkName[activeChainId],
      page: pageParam,
      categoryId: selectedMySalesCollection?.categoryId,
    });

    return { ...res.data, from: (pageParam - 1) * 30, nextPage: pageParam + 1 };
  };

  const {
    isLoading: isSingleItemsLoading,
    data: singleItems,
    hasNextPage: hasSingleItemsNextPage,
    fetchNextPage: fetchSingleItemsNextPage,
    error: singleItemsError,
  } = useInfiniteQuery(
    [
      'mysales-singleitem',
      account,
      isSwitchBundle,
      selectedMySalesOrderFilter,
      selectedMySalesCollection,
    ],
    fetchSingleItemList,
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.total > lastPage.from + 30) {
          return lastPage.nextPage;
        }
      },
      enabled: !!account && !!activeChainId && !isSwitchBundle,
      refetchOnWindowFocus: false,
    },
  );

  const {
    isLoading: isBundleItemsLoading,
    data: bundleItems,
    hasNextPage: hasBundleItemsNextPage,
    fetchNextPage: fetchBundleItemsNextPage,
    error: bundleItemsError,
  } = useInfiniteQuery(
    ['mysales-bundleitem', account, selectedMySalesCollection, isSwitchBundle],
    fetchBundleItemList,
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.total && lastPage.total > lastPage.from + 30) {
          return lastPage.nextPage;
        }
      },
      enabled: !!account && !!activeChainId && isSwitchBundle,
      refetchOnWindowFocus: false,
    },
  );

  const handleSwitchBundle = (switchState: boolean) => () => {
    dispatch(setIsSwitchBundle(switchState));
  };

  const handleNextPage = useCallback(async () => {
    !isSwitchBundle &&
      hasSingleItemsNextPage &&
      (await fetchSingleItemsNextPage());
    isSwitchBundle &&
      hasBundleItemsNextPage &&
      (await fetchBundleItemsNextPage());
  }, [
    fetchSingleItemsNextPage,
    fetchBundleItemsNextPage,
    hasSingleItemsNextPage,
    hasBundleItemsNextPage,
    isSwitchBundle,
  ]);

  const hanadleOrderFilterClick = (orderState: OrderStatus) => () => {
    const newOrderFilter = { ...selectedMySalesOrderFilter };
    newOrderFilter[orderState] = !selectedMySalesOrderFilter[orderState];

    dispatch(setSelectedMySalesOrderFilter(newOrderFilter));
  };

  const handleCollectionItemClick = (categoryId: number) => () => {
    if (!categoryId) {
      dispatch(setSelectedMySalesCollection(null));
    }
    if (activeChainId) {
      const activeCollection = collectionList[
        chainIdToMainNetworkName[activeChainId]
      ].find((category) => category.categoryId === categoryId);

      if (activeCollection) {
        dispatch(setSelectedMySalesCollection(activeCollection));
      }
      setIsClose(true);
    }
  };

  useEffect(() => {
    if (isClose) {
      setIsClose(() => false);
    }
  }, [isClose]);

  useEffect(() => {
    if (isSwitchBundle) {
      dispatch(setSelectedMySalesCollection(null));
    }
  }, [isSwitchBundle, dispatch]);

  useEffect(() => {
    if (loadMoreRefInView) {
      handleNextPage();
    }
  }, [loadMoreRefInView, handleNextPage]);

  useEffect(() => {
    dispatch(setIsSwitchBundle(salesType === 'bundle'));
  }, [dispatch, salesType]);

  return (
    <>
      <MetaTag title="My Sales | PlayDapp" />
      <SelectOptionArea>
        <SalesOptionBox>
          {!isSwitchBundle && (
            <div>
              <CustomButton
                className={classNames({
                  selected: selectedMySalesOrderFilter.offer,
                })}
                onClick={hanadleOrderFilterClick('offer')}
                size="xs"
                variant="outline"
              >
                <Typography color="primary700" type={isMobile ? 'b4' : 'p3'}>
                  Has Offer
                </Typography>
              </CustomButton>
            </div>
          )}
        </SalesOptionBox>

        <SelectBundleArea>
          <SelectBundleBox>
            <SelectBundleItem
              color="primary"
              variant={isSwitchBundle ? 'ghost' : 'solid'}
              size="xs"
              onClick={handleSwitchBundle(false)}
            >
              <Icon
                name={`${isSwitchBundle ? 'square-blank' : 'square-white'}`}
                size={14}
              />
              <Typography
                type="p3"
                component="p"
                color={isSwitchBundle ? 'gray900' : 'white'}
              >
                single
              </Typography>
            </SelectBundleItem>

            <SelectBundleItem
              color="primary"
              variant={!isSwitchBundle ? 'ghost' : 'solid'}
              size="xs"
              onClick={handleSwitchBundle(true)}
            >
              <Icon
                name={`${!isSwitchBundle ? 'grid-blank' : 'grid-white'}`}
                size={14}
              />
              <Typography
                type="p3"
                component="p"
                color={!isSwitchBundle ? 'gray900' : 'white'}
              >
                bundle
              </Typography>
            </SelectBundleItem>
          </SelectBundleBox>

          <CollectionDrop
            title={selectedMySalesCollection?.title ?? 'All Collection'}
            width="262"
            height="36"
            top="48"
            isBorder
            isDropBox
            floatingContent
            isClose={isClose}
            disabled={isSwitchBundle}
          >
            <DropDownSortContent>
              <DropDownItem onClick={handleCollectionItemClick(0)}>
                <Image
                  prefix="s3"
                  src="/collections/all.png"
                  alt="all"
                  width={24}
                  height={24}
                />
                <Typography type="p3" color="dgray400" component="p">
                  All Collection
                </Typography>
              </DropDownItem>
              {activeChainId &&
                collectionList[chainIdToMainNetworkName[activeChainId]].map(
                  (category) => (
                    <DropDownItem
                      key={category.categoryId}
                      onClick={handleCollectionItemClick(category.categoryId)}
                    >
                      <Image
                        src={category.thumbnailUrl}
                        alt="collection"
                        width={24}
                        height={24}
                      />
                      <Typography type="p3" color="dgray400" component="p">
                        {category.title}
                      </Typography>
                    </DropDownItem>
                  ),
                )}
            </DropDownSortContent>
          </CollectionDrop>

          <MobileCollectionDrop
            title={selectedMySalesCollection?.title ?? 'All Collection'}
            width="100%"
            height="40"
            top="40"
            isBorder
            floatingContent
            isMobile
            isClose={isClose}
            disabled={isSwitchBundle}
          >
            <DropDownSortContent>
              <DropDownItem onClick={handleCollectionItemClick(0)}>
                <Image
                  prefix="s3"
                  src="/collections/all.png"
                  alt="all"
                  width={24}
                  height={24}
                />
                <Typography type="p3" color="dgray400" component="p">
                  All Collection
                </Typography>
              </DropDownItem>
              {activeChainId &&
                collectionList[chainIdToMainNetworkName[activeChainId]].map(
                  (category) => (
                    <DropDownItem
                      key={category.categoryId}
                      onClick={handleCollectionItemClick(category.categoryId)}
                    >
                      <Image
                        src={category.thumbnailUrl}
                        alt="collection"
                        width={24}
                        height={24}
                      />
                      <Typography type="p3" color="dgray400" component="p">
                        {category.title}
                      </Typography>
                    </DropDownItem>
                  ),
                )}
            </DropDownSortContent>
          </MobileCollectionDrop>
        </SelectBundleArea>
      </SelectOptionArea>

      {!isSwitchBundle && (
        <ContentBody>
          <CardListBox>
            {isSingleItemsLoading &&
              times(30, (index) => <Card key={index} isLoading />)}
            {singleItems?.pages.map((items) => {
              const { assets } = items;

              return Object.values(assets).map(
                (
                  {
                    name,
                    imageUri,
                    animationUri,
                    tokenId,
                    network,
                    assetAddress,
                    owner,
                    order,
                    likeCount,
                    collectionInfo,
                  },
                  index,
                ) => (
                  <Card
                    key={`${name}_${tokenId}-${index}`}
                    collectionInfo={collectionInfo}
                    imageUri={imageUri}
                    animationUri={animationUri}
                    tokenId={tokenId}
                    name={name}
                    order={order}
                    convertedPrice={convertToUsd(
                      order.sale?.price,
                      network,
                      order.sale?.currency,
                    )}
                    like={numberToAbbreviateString(likeCount)}
                    network={network}
                    href={`/item/${network}/${assetAddress}/${tokenId}`}
                    assetAddress={assetAddress}
                    isOwner={owner === account}
                  />
                ),
              );
            })}

            {hasSingleItemsNextPage && (
              <>
                <Card isLoading ref={loadMoreRef} />
                {times(9, (index) => (
                  <Card key={index} isLoading />
                ))}
              </>
            )}
          </CardListBox>

          {!singleItemsError &&
            singleItems?.pages &&
            singleItems?.pages.length > 0 &&
            singleItems?.pages[0].total === 0 && (
              <EmptyItemBlock>
                <EmptyItem />
              </EmptyItemBlock>
            )}

          {!isSingleItemsLoading && singleItemsError && (
            <EmptyItemBlock>
              <ErrorContents />
            </EmptyItemBlock>
          )}
        </ContentBody>
      )}

      {isSwitchBundle && (
        <ContentBody>
          <CardListBox>
            {isBundleItemsLoading &&
              times(30, (index) => <Card key={index} isLoading />)}

            {bundleItems?.pages.map((items) => {
              const { bundles } = items;

              if (!bundles) {
                return null;
              }

              return Object.values(bundles).map(
                (
                  { name, slug, imageUri, network, likeCount, owner, order },
                  index,
                ) => (
                  <Card
                    key={`${name}_${slug}-${index}`}
                    imageUri={imageUri}
                    name={name}
                    order={order}
                    convertedPrice={convertToUsd(
                      order.sale?.price,
                      network,
                      order.sale?.currency,
                    )}
                    like={numberToAbbreviateString(likeCount)}
                    network={network}
                    href={`/bundle/${network}/${slug}`}
                    isOwner={owner === account}
                  />
                ),
              );
            })}

            {hasBundleItemsNextPage && (
              <>
                <Card isLoading ref={loadMoreRef} />
                {times(9, (index) => (
                  <Card key={index} isLoading />
                ))}
              </>
            )}
          </CardListBox>

          {!bundleItemsError &&
            bundleItems?.pages &&
            bundleItems?.pages.length > 0 &&
            bundleItems?.pages[0].total === 0 && (
              <EmptyItemBlock>
                <EmptyItem />
              </EmptyItemBlock>
            )}

          {!isBundleItemsLoading && bundleItemsError && (
            <EmptyItemBlock>
              <ErrorContents />
            </EmptyItemBlock>
          )}
        </ContentBody>
      )}
    </>
  );
};

export default MySales;
